import { useCallback, type ComponentProps } from "react";
import type { Field } from "../../../../api/types/objectTypes";
import FieldValuesEditorView from "../../../entityFields/FieldValuesEditorView";
import type { FieldValuesState } from "../../../entityFields/FieldValuesContext";
import { getFieldValueLabel, getLazyLookupOptionsType } from "../../../entityFields/helpers";
import type RelatedObjectsGroup from "../../../entityFields/RelatedObjectsGroup";
import useFieldValuesEditor, { type FieldValuesSectionProps } from "../../../entityFields/useFieldValuesEditor";
import type { ObjectCardEditChecks } from "../../../entityFields/useObjectCardChecks";
import type { InvoiceCardPreviewConfig } from "./invoiceCardPreviewState";
import { withReadOnlyFields, type InvoiceCardSource } from "./invoiceCardSource";
import InvoiceRelatedObjectsGroup from "./InvoiceRelatedObjectsGroup";
import useInvoiceCardPreview from "./useInvoiceCardPreview";

interface Props extends FieldValuesSectionProps {
  cardPreview?: InvoiceCardPreviewConfig;
  source?: InvoiceCardSource;
  onEditChecksChange: (checks: ObjectCardEditChecks) => void;
}

const InvoiceFieldValuesSection = ({ cardPreview, source, onEditChecksChange, ...params }: Props) => {
  const { objectId, fields, fieldValues, lookupObjects } = params;

  const adjustRelatedObjectFields = useCallback(
    (fieldsByObjectType: Record<string, Field[]>) => {
      const fieldsWithSource = source && fieldsByObjectType[source.relatedObjectType];
      return fieldsWithSource
        ? {
            ...fieldsByObjectType,
            [source.relatedObjectType]: withReadOnlyFields(fieldsWithSource, source.readOnlyRelatedFieldIds),
          }
        : fieldsByObjectType;
    },
    [source]
  );

  const editor = useFieldValuesEditor(params, adjustRelatedObjectFields);

  const handleRowDefaults = (fieldIds: string[]) => {
    const previewedFields = editor.relatedObjectFieldsByObjectType[cardPreview?.relatedObjectType ?? ""] ?? [];
    const lazyOptionsTypes = previewedFields
      .filter((field) => fieldIds.includes(field.id))
      .map(getLazyLookupOptionsType);
    void editor.handleLookupOptionsNeeded(lazyOptionsTypes.filter((type) => type !== undefined));
  };

  const preview = useInvoiceCardPreview({
    config: cardPreview,
    objectId,
    storedFieldValues: fieldValues,
    fieldValues: editor.fieldValuesState.fieldValues,
    storedRelatedObjects: editor.relatedObjectsResp?.relatedObjectsByType,
    relatedObjectValuesState: editor.relatedObjectValuesState,
    setRelatedObjectValuesState: editor.setRelatedObjectValuesState,
    onFieldDefaults: editor.applyFieldValues,
    onRowDefaults: handleRowDefaults,
    onEditChecksChange,
  });

  const { fieldValuesState } = editor;

  const handleFieldValuesStateChangeReportingPreview = (stateUpdate: Partial<FieldValuesState>) => {
    if (stateUpdate.fieldValues && fieldValuesState.fieldEditState !== "canceling") {
      preview.reportUserChange(undefined, fieldValuesState.fieldValues, stateUpdate.fieldValues, (fieldId, value) =>
        getFieldValueLabel(
          fields,
          fieldId,
          value,
          editor.lookupOptionsByObjectType ?? {},
          lookupObjects?.[fieldId] ?? []
        )
      );
    }

    editor.handleFieldValuesStateChange(stateUpdate);
  };

  const handleCancelClickResettingPreview = () => {
    editor.handleCancelClick();
    preview.reset();
  };

  const renderRelatedObjectsGroup = (groupProps: ComponentProps<typeof RelatedObjectsGroup>) => (
    <InvoiceRelatedObjectsGroup
      {...groupProps}
      invoiceFields={fields}
      invoiceFieldValues={fieldValuesState.fieldValues}
      cardPreview={cardPreview}
      sourceRelatedObjectType={source?.relatedObjectType}
      previewFieldValues={preview.fieldValues}
      cellDecorations={preview.cellDecorations}
      noOptionsTexts={preview.relatedNoOptionsTexts}
      hasPreviewFailed={preview.hasFailed}
      onUserRowChange={preview.reportUserChange}
    />
  );

  return (
    <FieldValuesEditorView
      {...params}
      editor={{
        ...editor,
        handleFieldValuesStateChange: handleFieldValuesStateChangeReportingPreview,
        handleCancelClick: handleCancelClickResettingPreview,
      }}
      isWaitingForValues={preview.isWaitingForDefaults}
      fieldsAutoFillTooltips={preview.fieldsAutoFillTooltips}
      renderRelatedObjectsGroup={renderRelatedObjectsGroup}
    />
  );
};

export default InvoiceFieldValuesSection;
